import React, { useMemo, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Popover } from 'react-native-popper'

import { isEqual } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  ClientFiltersPopover,
  ClientFiltersState,
  ClientFilterType,
  INITIAL_FILTER_STATE,
} from './ClientFiltersPopover'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { XIcon } from '../../atoms/icons/XIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { TextButton } from '../../atoms/textButton/TextButton'
import { BodyTextSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

const Container = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
  alignItems: 'center',
}))

const Button = styled(PressableOpacity)(({ theme }) => ({
  paddingTop: '11px', // To match the height of the InputField
  paddingBottom: '11px', // To match the height of the InputField
  paddingLeft: theme.spacing['16px'],
  paddingRight: theme.spacing['16px'],
  border: `1px solid ${theme.colors.borderActive}`,
  borderRadius: '4px',
  alignSelf: 'flex-start',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['4px'],
}))

const ActiveFilterCount = styled(BodyText)(({ theme }) => ({
  width: '24px',
  height: '24px',
  backgroundColor: theme.colors.backgroundActive,
  borderRadius: '4px',
  textAlign: 'center',
  lineHeight: '24px',
}))

const ClearFiltersButton = styled(TextButton)({
  alignSelf: 'center',
})

export * from './ClientFiltersPopover'

export type ClientFiltersProps = {
  programs?: string[]
  filterState: ClientFiltersState
  onFilterStateChange: (filterState: ClientFiltersState) => void
  availableFilters: ClientFilterType[]
  isEngageProvider?: boolean
}

export const ClientFilters: React.FC<ClientFiltersProps> = ({
  programs,
  filterState,
  onFilterStateChange,
  availableFilters,
  isEngageProvider,
}) => {
  const ref = useRef(null)
  const { colors } = useTheme()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const activeFilters = useMemo(
    () =>
      Object.entries(filterState).filter(
        ([key, value]) => !isEqual(INITIAL_FILTER_STATE[key as keyof ClientFiltersState], value),
      ).length,
    [filterState],
  )
  return (
    <Container>
      <Popover
        trigger={ref}
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        placement='bottom left'
        animated={!window.Cypress}
      >
        <Popover.Content>
          {(!window.Cypress || isPopoverOpen) && (
            <ClientFiltersPopover
              programs={programs}
              filterState={filterState}
              onFilterStateChange={onFilterStateChange}
              onClose={() => setIsPopoverOpen(false)}
              availableFilters={availableFilters}
              isEngageProvider={isEngageProvider}
            />
          )}
        </Popover.Content>
      </Popover>
      <Button
        testID={tID('ClientFilters-filter-button')}
        pressableRef={ref}
        onPress={() => setIsPopoverOpen((prev) => !prev)}
      >
        <BodyText text={<FormattedMessage defaultMessage='Filters' description='Filters button' />} />
        {activeFilters > 0 && (
          <ActiveFilterCount
            testID={tID('ClientFilters-active-filter-count')}
            text={activeFilters}
            size={BodyTextSize.BADGE}
            color={colors.textInverse}
          />
        )}
        <ChevronIcon
          size={24}
          direction={isPopoverOpen ? ChevronIconDirection.UP : ChevronIconDirection.DOWN}
          fillColor={colors.iconDefault}
        />
      </Button>
      {activeFilters > 0 && (
        <ClearFiltersButton
          testID={tID('ClientFilters-clear-filters-button')}
          leftIcon={<XIcon fillColor={colors.iconActive} size={24} />}
          text={<FormattedMessage defaultMessage='Clear filters' description='Clear filters button' />}
          onPress={() => onFilterStateChange(INITIAL_FILTER_STATE)}
        />
      )}
    </Container>
  )
}
